import { Button, ButtonVariants, Text, TextAs, TextSizes } from '@clockout-dev/design-elements';
import InfiniteScroll from '../infiniteScroll/InfiniteScroll';
import PayrollConnectionEventRow from './PayrollConnectionEventRow';
import { formatReportingTimezoneCaption } from '../../../utils/datetime';
import { getEventRowKey } from './payrollConnections.constants';
import styles from './styles/payroll-connections.module.scss';

/**
 * The event rows themselves, shared by the card's five-row preview and the
 * full-history modal.
 *
 * Extracted so those two surfaces can never drift: they show the same rows,
 * with the same outcome pill and the same one-per-list timezone caption, and
 * only differ in how many rows they pass and what sits underneath them.
 *
 * What a row looks like is `PayrollConnectionEventRow`'s business; this file
 * owns what surrounds them — the caption, the paging and the end-of-list state.
 *
 * Paging is opt-in: pass `onLoadMore` and the list grows by scrolling, with a
 * bottom sentinel that asks for the next page as it comes into view. The card
 * preview passes nothing, so it never observes and never fetches — its rows are
 * a slice of what is already in memory.
 *
 * @param {Object} props
 * @param {Array<Object>} props.items Events, newest first.
 * @param {string} [props.timezone] Declared zone from the payload.
 * @param {string} [props.ariaLabel] Accessible name for the list.
 * @param {string} [props.heading] Visible heading above the rows. Defaults to
 *   the card preview's wording; the modal overrides it, because everything
 *   loaded lives there and "Last events" would understate it.
 * @param {boolean} [props.hasMore] The endpoint still has pages.
 * @param {boolean} [props.loadingMore] A page is in flight.
 * @param {Object | null} [props.moreError] The last page request failed. Reported
 *   under the rows, never in place of them.
 * @param {() => void} [props.onLoadMore] Enables scroll paging when given; also
 *   the retry for `moreError`.
 * @param {HTMLElement | null} [props.scrollRoot] Scrolling ancestor; omit for the viewport.
 */
function PayrollConnectionEventList({
    items,
    timezone,
    ariaLabel,
    heading = 'Last events',
    hasMore = false,
    loadingMore = false,
    moreError = null,
    onLoadMore,
    scrollRoot = null,
}) {
    const rows = (
        <ul className={styles.eventList} aria-label={ariaLabel}>
            {items.map((event, index) => (
                <PayrollConnectionEventRow key={getEventRowKey(event, index)} event={event} />
            ))}
        </ul>
    );

    return (
        <>
            <Text as={TextAs.H4} size={TextSizes.SMALL}>
                {heading}
            </Text>

            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.timezoneNote}>
                {formatReportingTimezoneCaption(timezone)}
            </Text>

            {onLoadMore ? (
                <InfiniteScroll
                    onEndReached={onLoadMore}
                    hasMore={hasMore}
                    loading={loadingMore}
                    scrollRoot={scrollRoot}
                    // Short gate before the sentinel is observed: on open the
                    // dialog is still sizing itself, and an observer installed
                    // against a zero-height scroll root reads the sentinel as
                    // visible and fetches a page nobody scrolled for.
                    initialDelayMs={200}
                    rootMargin="0px 0px 240px 0px"
                >
                    {rows}
                    {/* The end of the list is never silent: a page is coming,
                        the last one failed, or the record is complete. Without
                        this the list just stops, which reads the same as "that
                        is all" — and a failed page would be the most dishonest
                        version of that, since more events demonstrably exist. */}
                    <div className={styles.eventListStatus} aria-live="polite">
                        {moreError ? (
                            <div className={styles.eventListError}>
                                <Text
                                    as={TextAs.SPAN}
                                    size={TextSizes.EXTRA_SMALL}
                                    className={styles.eventListErrorText}
                                >
                                    Couldn&apos;t load more events.
                                </Text>
                                {onLoadMore ? (
                                    <Button
                                        variant={ButtonVariants.DEFAULT}
                                        className={styles.retryButton}
                                        onClick={() => onLoadMore()}
                                    >
                                        Retry
                                    </Button>
                                ) : null}
                            </div>
                        ) : (
                            <Text as={TextAs.SPAN} size={TextSizes.EXTRA_SMALL} className={styles.eventListStatusText}>
                                {loadingMore
                                    ? 'Loading more events…'
                                    : hasMore
                                      ? 'Scroll for more events'
                                      : 'End of the connection record'}
                            </Text>
                        )}
                    </div>
                </InfiniteScroll>
            ) : (
                rows
            )}
        </>
    );
}

export default PayrollConnectionEventList;
